'use client'

import { Branch } from '@/types/branch'
import { FeedListItem } from '@/types/feed'
import {
  Avatar,
  Box,
  Center,
  Container,
  Flex,
  Heading,
  Icon,
  IconButton,
  Img,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Badge,
  AspectRatio,
} from '@/components/Chakra'
import { HiDotsHorizontal, HiLocationMarker, HiOfficeBuilding } from 'react-icons/hi'
import { getPrefText } from '@/lib/pref'
import { BiLinkExternal } from 'react-icons/bi'
import { COMPANY_VIOLATION_REPORT_URL } from '@/config'
import { BranchAbout } from './BranchAbout'
import { BranchFeeds } from './BranchFeeds'
import { BranchJobs } from './BranchJobs'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { FetcherResponse } from '@/types/api-response'

type Props = {
  branch: Branch
  feeds: FetcherResponse<FeedListItem[]>
}

export const BranchDetail = ({ branch, feeds }: Props) => {
  const serachParams = useSearchParams()
  const [tabIndex, setTabIndex] = useState<number>(Number(serachParams.get('tab')) || 0)
  const [jobsTotal, setJobsTotal] = useState(0)

  const contentMaxWidth = [
    [811, 811, 811],
    [620, 620, 620],
    [620, 620, 620],
  ]

  const tabNames: { [key in number]: string } = {
    0: 'about',
    1: 'feeds',
    2: 'jobs',
  }

  useEffect(() => {
    let tabIndex = Object.values(tabNames).findIndex((tabName) => tabName == serachParams.get('tab'))
    tabIndex = tabIndex == -1 ? 0 : tabIndex
    setTabIndex(tabIndex)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serachParams.get('tab')])

  const handleTabChange = (tabIndex: number) => {
    setTabIndex(tabIndex)
    window.scrollTo({ top: 0 })
  }

  return (
    <>
      <Box mt={[-5, -5, -12]}>
        <Box
          className={'bg-filter'}
          height={'auto'}
          background={`url(${branch.company.cover_url})`}
          backgroundSize={'cover'}
          backgroundPosition={'center center'}
        >
          <Container maxW={[892, 892, 892]}>
            <AspectRatio ratio={892 / 321}>
              <Img width={'100%'} height={'321px'} objectFit={'cover'} src={branch.company.cover_url} alt={''} />
            </AspectRatio>
          </Container>
        </Box>
        <Container mt={6} maxW={[811, 811, 811]}>
          <Flex direction={'row'}>
            <Avatar
              showBorder={true}
              border={'1px solid #E6E9ED'}
              mr={4}
              size={['lg', 'lg', 'xl']}
              src={branch.company.thumbnail_url}
            />
            <Center>
              <Stack direction={'column'}>
                <Heading fontSize={'2xl'} as={'h1'}>
                  {branch.company.name + (branch.name || '')}
                </Heading>
                <Stack direction={'row'} align={'center'} color={'gray.500'}>
                  <Icon color={'gray.400'} as={HiLocationMarker} />
                  <Text fontSize={['xs', 'xs', 'sm']}>
                    {getPrefText(branch.pref_id)}
                    {branch.city_name}
                  </Text>
                  <Icon ml={6} color={'gray.400'} as={HiOfficeBuilding} />
                  <Text fontSize={['xs', 'xs', 'sm']}>{branch.company.industry.name}</Text>
                </Stack>
              </Stack>
            </Center>
            <Spacer />
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="Options"
                icon={<HiDotsHorizontal />}
                variant={'ghost'}
                fontSize={'xl'}
                color={'gray.500'}
              />
              <MenuList>
                <MenuItem
                  fontSize={'lg'}
                  color={'primary.500'}
                  as={'a'}
                  target={'_blank'}
                  href={COMPANY_VIOLATION_REPORT_URL}
                >
                  <Stack direction={'row'} align={'center'}>
                    <Text fontSize={'md'} color={'#0D0D0D'} as={'span'}>
                      この会社を報告する
                    </Text>
                    <BiLinkExternal />
                  </Stack>
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Container>
        <Container p={0} maxW={contentMaxWidth[tabIndex]}>
          <Tabs mt={3} size={'lg'} index={tabIndex} defaultIndex={tabIndex} onChange={handleTabChange}>
            <Center bg={'white'} position={'sticky'} top={0} background={'white'} zIndex={1299}>
              <Box boxShadow={'sm'} position={'absolute'} height={'1px'} width={'100vw'} bottom={0}></Box>
              <TabList borderBottom={'2px solid'} borderColor={'gray.200'}>
                <Tab
                  fontSize={'md'}
                  color={tabIndex == 0 ? 'primary.500 !important' : 'gray.800'}
                  fontWeight={'normal'}
                  w={['auto', 'auto', '176px']}
                >
                  企業トップ
                </Tab>
                <Tab
                  fontSize={'md'}
                  color={tabIndex == 1 ? 'primary.500 !important' : 'gray.800'}
                  fontWeight={'normal'}
                  w={['auto', 'auto', '176px']}
                >
                  フィード
                </Tab>
                <Tab
                  fontSize={'md'}
                  color={tabIndex == 2 ? 'primary.500 !important' : 'gray.800'}
                  fontWeight={'normal'}
                  w={['auto', 'auto', '176px']}
                >
                  募集一覧
                  <Badge
                    fontSize={'xs'}
                    ml={1}
                    padding={'2px 10px'}
                    variant={'ellipse'}
                    bg={tabIndex == 2 ? 'primary.500' : 'gray.500'}
                    color={'white'}
                  >
                    {jobsTotal}
                  </Badge>
                </Tab>
              </TabList>
            </Center>
            <TabPanels>
              <TabPanel>
                <BranchAbout handleTabChange={handleTabChange} branch={branch} feeds={feeds.data} />
              </TabPanel>
              <TabPanel>
                <BranchFeeds branch={branch} initialItems={feeds.data} totalPages={feeds.headers?.totalPages} />
              </TabPanel>
              <TabPanel>
                <BranchJobs branch={branch} setTotal={setJobsTotal} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Container>
      </Box>
    </>
  )
}
